import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { LogOut, Menu, X } from "lucide-react";
import { useState } from "react";

export function TopBar({ children }) {
  const { user, logout } = useAuth();
  return (
    <header className="glass-header sticky top-0 z-30">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-4 lg:gap-12 min-w-0">
          <Link to="/" className="flex items-center gap-3 min-w-0" data-testid="app-logo">
            {/* Primary mark — VI Business Academy */}
            <img
              src="/brand/vi-academy-logo-light.png"
              alt="Vi Business Academy"
              className="h-12 sm:h-16 object-contain flex-shrink-0"
              data-testid="app-logo-vi"
            />
            {/* Secondary mark — powered by Mercury Compass Assess */}
            <div className="hidden md:flex flex-col leading-tight pl-3 border-l border-[#E4E5EB]">
              <span className="text-[8px] uppercase tracking-[0.18em] text-[#8A8B9D] font-semibold">Powered by</span>
              <span className="text-[11px] uppercase tracking-[0.14em] text-[#5F004B] font-bold">Mercury Compass Assess</span>
            </div>
          </Link>
          {children}
        </div>
        <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
          <div className="text-right leading-tight hidden md:block">
            <div className="text-[13px] font-semibold text-[#1A1A24]" data-testid="user-name">{user?.name}</div>
            <div className="text-[11px] text-[#8A8B9D] uppercase tracking-[0.15em] font-semibold">{user?.role?.replace("_", " ")}</div>
          </div>
          <button
            onClick={logout}
            className="btn btn-ghost !px-3 !py-2"
            data-testid="logout-button"
            title="Logout"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </header>
  );
}

export function NavTabs({ tabs }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const activeTab = tabs.find((t) => pathname === t.to || (t.to !== "/" && pathname.startsWith(t.to)));

  return (
    <>
      {/* Desktop tabs */}
      <div className="hidden md:flex items-center gap-1 flex-wrap" data-testid="nav-desktop">
        {tabs.map((t) => {
          const active = pathname === t.to || (t.to !== "/" && pathname.startsWith(t.to));
          return (
            <Link
              key={t.to}
              to={t.to}
              data-testid={`nav-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-3 py-1.5 text-[13px] font-semibold rounded-sm transition-colors ${
                active ? "bg-[#5F004B] text-white" : "text-[#535465] hover:text-[#5F004B] hover:bg-[#F0F1F5]"
              }`}
            >
              {t.label}
            </Link>
          );
        })}
      </div>

      {/* Mobile hamburger */}
      <button
        onClick={() => setOpen(!open)}
        className="md:hidden flex items-center gap-2 px-3 py-1.5 text-[12px] font-semibold text-[#0A0A0A] border border-[#E5E7EB] rounded-sm"
        data-testid="nav-mobile-toggle"
      >
        {open ? <X size={14} /> : <Menu size={14} />}
        {activeTab?.label || "Menu"}
      </button>
      {open && (
        <div className="md:hidden fixed inset-x-0 top-16 bg-white border-b border-[#E5E7EB] shadow-lg z-40" data-testid="nav-mobile-drawer">
          <div className="px-4 py-2 flex flex-col">
            {tabs.map((t) => {
              const active = pathname === t.to || (t.to !== "/" && pathname.startsWith(t.to));
              return (
                <Link
                  key={t.to}
                  to={t.to}
                  onClick={() => setOpen(false)}
                  className={`px-3 py-3 text-sm font-semibold border-b border-[#F3F4F6] last:border-b-0 ${
                    active ? "text-[#EE2737]" : "text-[#0A0A0A]"
                  }`}
                  data-testid={`nav-m-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {t.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
